import { PortfolioAsset } from './portfolio.model';
import { EffectivePriceBasis, PortfolioPriceBasis } from './user-setting.model';

/**
 * مبنای قیمت as it applies right now: خودکار follows the market — آخرین قیمت
 * while the session is open, قیمت پایانی outside it — and the other two are
 * already the answer.
 */
export function resolvePriceBasis(basis: PortfolioPriceBasis, marketOpen: boolean): EffectivePriceBasis {
	if (basis !== 'auto') {
		return basis;
	}

	return marketOpen ? 'lastTradedPrice' : 'closingPrice';
}

/**
 * The price this holding's value and profit/loss are measured at, per the basis
 * it carries.
 *
 * Scenario 5 — نبود قیمت معتبر: a basis whose price is not known falls back to
 * the other one rather than valuing the holding at 0. A certificate that has not
 * traded today still has a settlement price behind it, and a portfolio reporting
 * ۰ ریال because one feed is quiet is a worse answer than one measured at the
 * price we do have. With neither known the figure is 0, and `revalue` leaves the
 * API's own numbers standing instead.
 */
export function getValuationPrice(asset: PortfolioAsset): number {
	return asset.priceBasis === 'closingPrice' ? asset.closingPrice || asset.price : asset.price || asset.closingPrice;
}

/**
 * خالص ارزش فروش — what the remaining quantity fetches at the price the trader
 * chose as the basis, net of the commission charged on the sale:
 * `Q × P × (1 − SCR)`.
 */
export function getNetSaleValue(asset: PortfolioAsset): number {
	return asset.quantity * getValuationPrice(asset) * (1 - asset.sellCommissionRate);
}

/**
 * بهای تمام‌شده — what the remaining quantity cost at its buy average: `Q × ABP`.
 *
 * The buy commission is deliberately not charged on top. `averageBuyPrice` is
 * the average the platform recorded for the position, and the basis is measured
 * at that price alone; only the sell commission enters the figures, because it is
 * still owed on liquidating the holding.
 *
 * A holding with no positive buy average has no measurable basis, so it is null
 * rather than 0 — see `totalCost` and {@link getTotalProfitLossPercent}.
 */
export function getTotalCost(asset: PortfolioAsset): number | null {
	if (asset.averageBuyPrice <= 0) {
		return null;
	}
	return asset.quantity * asset.averageBuyPrice;
}

export function getBreakEvenPrice(averageBuyPrice: number, commission: number): number {
	if (averageBuyPrice <= 0) {
		return 0;
	}

	const sellCommissionRate = commission / 2;

	return Math.round(averageBuyPrice / (1 - sellCommissionRate));
}

/**
 * سود و زیان امروز — what the holding gained or lost this session: `Q × (P − LSP)`,
 * measured at the same basis as the rest of the card so the summary and the
 * cards read one price throughout (scenario 4).
 *
 * Unlike the position's own profit/loss this is gross, with no commission at
 * all: nothing is sold to realize a day's move, so charging a fee against it
 * would report a loss on a session that never traded.
 */
export function getTodayProfitLossValue(asset: PortfolioAsset): number {
	return asset.quantity * (getValuationPrice(asset) - asset.lastSettlementPrice);
}

/**
 * Recomputes the figures that follow from quantity, price, buy average and the
 * sell commission rate:
 *
 *     P&L = [Q × P × (1 − SCR)] − [Q × ABP]
 *
 * The sell commission belongs in the figure because it comes off whatever the
 * holding is liquidated for, so a gain that does not cover it is not a gain.
 * The basis is the buy average as recorded, with no fee added on top.
 *
 * With no price at all the formula would report the entire basis as a loss, so an
 * unpriced holding — and one with no cost basis — keeps the API's own
 * `totalProfit` instead. Today's figure follows the same rule against its own
 * reference: it needs a settlement price, not a cost basis.
 */
function revalue(asset: PortfolioAsset): PortfolioAsset {
	if (!getValuationPrice(asset)) {
		return asset;
	}

	const totalCost = getTotalCost(asset);
	const netSaleValue = getNetSaleValue(asset);
	const todayProfitLoss = asset.lastSettlementPrice ? getTodayProfitLossValue(asset) : asset.todayProfitLoss;

	// Without a basis there is nothing to measure the sale against, and treating
	// the missing cost as 0 would report the holding's whole value as profit. Such
	// a holding keeps the API's figure, and stays out of the portfolio percentage.
	if (totalCost === null) {
		return { ...asset, netSaleValue, totalCost, todayProfitLoss };
	}

	const profitLoss = netSaleValue - totalCost;

	return {
		...asset,
		netSaleValue,
		totalCost,
		todayProfitLoss,
		profitLoss,
		profitLossPercent: getProfitLossPercent(profitLoss, totalCost)
	};
}

/**
 * Applies the symbol's commission rate to a holding.
 *
 * The rate passed in is the sell-side one — the only rate the holding's figures
 * charge (see {@link getTotalCost}) — which the symbol snapshot reports as `sellCommission`.
 * Its round-trip `commission` covers the buy fee too and must not be used here:
 * charging all of it on the sale understated profit by one extra fee.
 */
export function withCommissionRate(asset: PortfolioAsset, sellCommission: number): PortfolioAsset {
	return revalue({ ...asset, sellCommissionRate: sellCommission });
}

/**
 * Profit/loss as a share of cost. A non-positive basis yields 0 rather than
 * Infinity or a sign-flipped figure: a holding with no recorded cost has no
 * meaningful percentage, and the card must not render `∞%`.
 */
export function getProfitLossPercent(profitLoss: number, totalCost: number): number {
	if (totalCost <= 0) {
		return 0;
	}
	return (profitLoss / totalCost) * 100;
}

/**
 * Applies a market-data price to a holding, and revalues it at that price.
 *
 * `lastSettlementPrice` is yesterday's close, which is what the session's move is
 * measured against — the same reference the market list uses, so a symbol shows
 * one change percent across both screens.
 *
 * A price of 0 means "not traded / not known" rather than "worth nothing". Any
 * price the holding already carries is kept over such a tick; a symbol that has
 * never traded is priced at its close, which is what the formula values an
 * untraded holding at — not 0.
 *
 * `priceChange` is the backend's own change, passed when seeding from the snapshot: it is
 * zeroed from midnight to the next open, which a change recomputed from yesterday's last
 * price would not be. A tick recomputes the change only when it carries a trade; one that
 * does not keeps the change the holding already shows.
 */
export function withPrice(
	asset: PortfolioAsset,
	price: number,
	lastSettlementPrice: number,
	closingPrice = 0,
	priceChange?: number
): PortfolioAsset {
	const effectivePrice = price || asset.price || lastSettlementPrice;
	// A quiet session leaves the closing price at the settlement price, which is
	// what an untraded certificate is worth today — the same rule
	// `mapMarketViewToClosingPrice` applies to a tick that carries no volume.
	const effectiveClosingPrice = closingPrice || asset.closingPrice || lastSettlementPrice;

	// Either price is enough to go on. Guarding on the traded price alone would
	// throw away a tick that carries only a VWAP — a newly listed certificate with
	// no settlement behind it and no trade yet this session — and leave a holding
	// the closing basis could have valued reporting 0.
	if (!effectivePrice && !effectiveClosingPrice) {
		return asset;
	}

	// A certificate that has never traded is priced at its close, which is what
	// the doc above promises and what the figures value an untraded holding at.
	// Before قیمت پایانی was carried, only the settlement price could stand in;
	// now the session's own VWAP does too, for a listing with no settlement yet.
	const lastTradedPrice = effectivePrice || effectiveClosingPrice;
	const change = priceChange ?? (price > 0 && lastSettlementPrice ? price - lastSettlementPrice : asset.priceChange);

	return revalue({
		...asset,
		price: lastTradedPrice,
		// The session's move stays measured against آخرین قیمت whatever the basis
		// is: it is the change beside the price the card prints, not a valuation.
		priceChange: change,
		priceChangePercent: lastSettlementPrice ? (change / lastSettlementPrice) * 100 : 0,
		lastSettlementPrice,
		closingPrice: effectiveClosingPrice
	});
}

/**
 * Measures a holding at `priceBasis`, recomputing every figure that follows from
 * the price — scenario 4's "خلاصه کل و تمام کارت‌ها را مجدداً محاسبه کند".
 *
 * A holding already on that basis is returned untouched, so a basis that has not
 * changed costs no allocation and leaves the list's `track` identities alone.
 */
export function withPriceBasis(asset: PortfolioAsset, priceBasis: EffectivePriceBasis): PortfolioAsset {
	return asset.priceBasis === priceBasis ? asset : revalue({ ...asset, priceBasis });
}

/**
 * Scenario 1 of the acceptance criteria: a card is shown for every asset with a
 * positive balance, so zero and negative quantities are excluded from the list.
 */
export function hasPositiveBalance(asset: PortfolioAsset): boolean {
	return asset.quantity > 0;
}

/**
 * ارزش کل پرتفوی — the net sale value of every holding.
 */
export function getTotalNetSaleValue(assets: readonly PortfolioAsset[]): number {
	return assets.reduce((total, asset) => total + asset.netSaleValue, 0);
}

/**
 * سود یا زیان کل پرتفوی — the summed profit/loss of every holding.
 */
export function getTotalProfitLoss(assets: readonly PortfolioAsset[]): number {
	return assets.reduce((total, asset) => total + asset.profitLoss, 0);
}

/**
 * درصد سود یا زیان کل, measured against what the holdings cost.
 *
 * Only holdings with a recorded cost take part. A holding without one has no
 * measurable return, and folding it in at its full value would drag the headline
 * percentage toward 0% however the rest of the portfolio performed.
 *
 * An empty portfolio, or one whose recorded costs do not sum to a positive
 * basis, is 0% rather than Infinity or a sign-flipped figure.
 */
export function getTotalProfitLossPercent(assets: readonly PortfolioAsset[]): number {
	const measurable = assets.filter(asset => asset.totalCost !== null);
	const totalCost = measurable.reduce((total, asset) => total + (asset.totalCost ?? 0), 0);

	return getProfitLossPercent(getTotalProfitLoss(measurable), totalCost);
}

/**
 * سود و زیان امروز — summed across the holdings, each one's figure being the
 * session's gross move on it (see {@link getTodayProfitLossValue}).
 */
export function getTodayProfitLoss(assets: readonly PortfolioAsset[]): number {
	return assets.reduce((total, asset) => total + asset.todayProfitLoss, 0);
}

/**
 * دارایی کل بورس‌کالا — ارزش پرتفوی plus the sum of all user wallet balances.
 */
export function getTotalAssets(assets: readonly PortfolioAsset[], depositBalance: number): number {
	return getTotalNetSaleValue(assets) + depositBalance;
}
